import { useEffect, useState } from 'react';
import NetworkStatusMessages from '../../../models/enums/NetworkStatusMessages';
import LocalServerStatusBar from './LocalServerStatusBar';
import StatusBar from 'common/components/StatusBar';
import {
  BannerInputs,
  DEFAULT_C5_THRESHOLD_MS,
  DEFAULT_RETURN_NOTICE_WINDOW_MS,
  HeldActivation,
  buildBannerInputs,
} from 'common/utils/offlineMode/bannerInputs';
import {
  BannerVariant,
  dismissibilityOf,
  resolveBanner,
} from 'common/utils/offlineMode/bannerPrecedence';
import {
  LOCAL_NETWORK_FRESHNESS_MS,
  isStandinDeploymentOrigin,
} from 'common/utils/offlineMode/availability';
import {
  ENTER_LOCAL_SERVER_LABEL,
  OFFLINE_MODE_BANNER_SLOT_TEST_ID,
  OFFLINE_MODE_KEEP_TAB_OPEN,
} from 'common/utils/offlineMode/constants';
import { navigableStandinUrl } from 'common/utils/navigableStandinUrl';
import { mobxInjectSelect } from 'common/utils';
import BEM from 'common/bem';

const classes = BEM.with('OfflineModeBannerSlot');

export const BANNER_STALENESS_TICK_MS = LOCAL_NETWORK_FRESHNESS_MS;

type Organization = { id: string; standInBaseURL?: string };

type MobxProps = {
  statesByOrg: Record<string, HeldActivation>;
  lastSuccessfulPull: (orgToken: string) => number | undefined;
  isStandinDeployment: boolean;
  currentOrganizationId: string | null;
  currentOrganization?: Organization;
  networkStatus: string;
  messageVisible: boolean;
  degradedSince: number | null;
};

type Presentation = 'localServer' | 'cloudHeld' | 'connectivity';

const PRESENTATION: Partial<Record<BannerVariant, Presentation>> = {
  O1: 'localServer',
  O2: 'localServer',
  C2: 'cloudHeld',
  C6: 'connectivity',
};

function nextExpiryMs(inputs: BannerInputs, lastPullMs: number | undefined): number | null {
  const candidates = [
    lastPullMs === undefined ? null : lastPullMs + LOCAL_NETWORK_FRESHNESS_MS + 1,
    inputs.degradedSinceMs === null ? null : inputs.degradedSinceMs + inputs.c5ThresholdMs,
    inputs.held === null || inputs.held.isActive
      ? null
      : inputs.held.updatedAt + inputs.returnNoticeWindowMs,
  ].filter((at): at is number => at !== null && at > inputs.nowMs);

  return candidates.length === 0 ? null : Math.min(...candidates);
}

function OfflineModeBannerSlot({
  statesByOrg,
  lastSuccessfulPull,
  isStandinDeployment,
  currentOrganizationId,
  currentOrganization,
  networkStatus,
  messageVisible,
  degradedSince,
}: MobxProps) {
  const [, setTick] = useState(0);

  const source = { isStandinDeployment, lastSuccessfulPull, statesByOrg };
  const nowMs = Date.now();
  const degraded = messageVisible && Boolean(NetworkStatusMessages[networkStatus]);

  const inputs = currentOrganizationId
    ? buildBannerInputs({
        orgToken: currentOrganizationId,
        store: source,
        nowMs,
        viewing: isStandinDeploymentOrigin(source) ? 'offline' : 'cloud',
        connectivity: { degraded },
        degradedSinceMs: degradedSince,
        orgProvisionedForOfflineMode: true,
        returnNoticeWindowMs: DEFAULT_RETURN_NOTICE_WINDOW_MS,
        c5ThresholdMs: DEFAULT_C5_THRESHOLD_MS,
      })
    : null;

  const expiresAt = inputs
    ? nextExpiryMs(
        inputs,
        currentOrganizationId ? lastSuccessfulPull(currentOrganizationId) : undefined
      )
    : null;

  useEffect(() => {
    if (expiresAt === null) return undefined;

    const delay = expiresAt - Date.now();
    if (delay <= 0) return undefined;

    const timer = setTimeout(() => setTick((previous) => previous + 1), delay);
    return () => clearTimeout(timer);
  }, [expiresAt]);

  const variant = inputs ? resolveBanner(inputs) : null;
  const presentation = variant ? PRESENTATION[variant] : undefined;
  if (!variant || !presentation) return null;

  if (presentation === 'localServer') {
    return (
      <LocalServerStatusBar
        bannerVariant={variant}
        data-test-id={OFFLINE_MODE_BANNER_SLOT_TEST_ID}
      />
    );
  }

  const dismissible = dismissibilityOf(variant) !== 'never';

  if (presentation === 'connectivity') {
    return (
      <StatusBar
        className={classes()}
        variant={networkStatus === 'ONLINE' ? 'success' : 'error'}
        data-test-id={OFFLINE_MODE_BANNER_SLOT_TEST_ID}
        data-variant={variant}
        onDismiss={dismissible ? () => setTick((previous) => previous + 1) : undefined}
      >
        <span className={classes('message')}>{NetworkStatusMessages[networkStatus]}</span>
      </StatusBar>
    );
  }

  const standinUrl = navigableStandinUrl(currentOrganization?.standInBaseURL);

  return (
    <StatusBar
      className={classes()}
      variant="warning"
      data-test-id={OFFLINE_MODE_BANNER_SLOT_TEST_ID}
      data-variant={variant}
      action={
        standinUrl ? (
          <a className={classes('link')} href={standinUrl}>
            {ENTER_LOCAL_SERVER_LABEL}
          </a>
        ) : undefined
      }
    >
      <span className={classes('message')}>{OFFLINE_MODE_KEEP_TAB_OPEN}</span>
    </StatusBar>
  );
}

export default mobxInjectSelect<Record<string, never>, MobxProps>({
  activationStateStore: ['statesByOrg', 'lastSuccessfulPull', 'isStandinDeployment'],
  messengerStore: ['currentOrganizationId', 'currentOrganization'],
  networkStatusStore: ['networkStatus', 'messageVisible', 'degradedSince'],
})(OfflineModeBannerSlot);
